/**
 * Activation state mapping (database stores integer)
 * 0 = pre      - User hasn't started activation
 * 1 = shallow  - User has run AutoStack at least once
 * 2 = deep     - User has reached deep activation threshold
 */
export const ACTIVATION_STATE = {
  PRE: 0,
  SHALLOW: 1,
  DEEP: 2,
} as const;

export type ActivationStateValue =
  (typeof ACTIVATION_STATE)[keyof typeof ACTIVATION_STATE];

// Human-readable labels for display
export const ACTIVATION_STATE_LABELS: Record<ActivationStateValue, string> = {
  [ACTIVATION_STATE.PRE]: 'Pre-activation',
  [ACTIVATION_STATE.SHALLOW]: 'Shallow',
  [ACTIVATION_STATE.DEEP]: 'Deep',
};

// Colors for state chips
export const ACTIVATION_STATE_COLORS: Record<
  ActivationStateValue,
  'default' | 'warning' | 'success'
> = {
  [ACTIVATION_STATE.PRE]: 'default',
  [ACTIVATION_STATE.SHALLOW]: 'warning',
  [ACTIVATION_STATE.DEEP]: 'success',
};

export type ChecklistVariant = 'sidebar' | 'floating' | 'banner';

export interface UserActivation {
  user_id: string;
  fbo_id: string;
  activation_mode: boolean;
  activation_started_at: string | null;
  activation_window_days: number;
  activation_state: ActivationStateValue; // 0, 1, or 2
  autostack_run_count: number;
  first_autostack_at: string | null;
  deep_activation_threshold: number;
  deep_activation_at: string | null;
  step_add_aircraft_at: string | null;
  step_first_autostack_at: string | null;
  step_second_autostack_at: string | null;
  step_save_favorite_at: string | null;
  step_share_layout_at: string | null;
  checklist_variant: ChecklistVariant;
  last_activity_at: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

// Checklist step definition
export interface ChecklistStep {
  id: string;
  titleKey: string; // i18n key for title
  descriptionKey: string; // i18n key for description
  completedAtField: keyof Pick<
    UserActivation,
    | 'step_add_aircraft_at'
    | 'step_first_autostack_at'
    | 'step_second_autostack_at'
    | 'step_save_favorite_at'
    | 'step_share_layout_at'
  >;
  action?: {
    type: 'navigate' | 'trigger';
    path?: string;
    hint?: string;
    event?: string; // Event name to emit for trigger type
    spotlightId?: string; // ID of element to spotlight when triggered
  };
}

// Props for checklist variant components
export interface ChecklistVariantProps {
  activation: UserActivation;
  steps: ChecklistStep[];
  currentStepIndex: number;
  completedCount: number;
  onStepClick: (step: ChecklistStep) => void;
  onDismiss?: () => void;
}
